import { DEFAULT_STRAP_DESIGN, audioSourceKey, editDurationMs, frameSize, isPieceSound, pieceSounds, sourceKey, type EditAudioSource, type EditProject, type SourceDurations } from '@airpost/shared';
import { AudioBufferSource, BufferTarget, CanvasSink, CanvasSource, Mp4OutputFormat, Output, Quality, getFirstEncodableAudioCodec, getFirstEncodableVideoCodec, type AudioCodec, type Input, type VideoCodec, type WrappedCanvas } from 'mediabunny';
import { breathe, decodeSound, loadStill, openSource, sourceUrlOf } from './edit-media.js';
import { scheduleMix } from './edit-mix.js';
import { frameAt, mixPlan, type FrameSpec } from './edit-plan.js';
import { FrameRenderer, type LayerImage, type RenderResult } from './edit-render.js';
import { loadStrapArt } from './strap-art.js';

/**
 * The export of an edit as an MP4, in the browser, faster than real time. The pictures: the frames of the edit at
 * its frame rate and in its format, each drawn exactly as the monitor draws it (`frameAt` and the same
 * `FrameRenderer`, transitions, looks, texts and pictures included), from the sources' frames decoded with
 * WebCodecs (Mediabunny) and the photos; then encoded (H.264 where the browser has it). The sound: the monitor's plan
 * (`mixPlan`) rendered on an offline Web Audio graph, encoded to AAC. Where WebCodecs is missing the page records the
 * monitor in real time instead.
 */
/** About 5 Mbps for 720p and 10 for 1080p: a newsroom proxy's rate. */
const bitrateFor = (w: number, h: number) => Math.round(((w * h) / (1280 * 720)) * 5_000_000 * 0.9 + 500_000);
const AUDIO_BITRATE = 160_000;

export interface FastCodecs {
  video: VideoCodec;
  audio: AudioCodec;
}

/** The codecs this browser can encode for an export of this size (720p when not said), or null when it has no WebCodecs. */
export async function fastExportCodecs(size = { w: 1280, h: 720 }): Promise<FastCodecs | null> {
  if (typeof VideoEncoder === 'undefined' || typeof AudioEncoder === 'undefined') return null;
  try {
    const video = await getFirstEncodableVideoCodec(['avc', 'vp9', 'av1'], { width: size.w, height: size.h });
    const audio = await getFirstEncodableAudioCodec(['aac', 'opus'], { numberOfChannels: 2, sampleRate: 48_000 });
    return video && audio ? { video, audio } : null;
  } catch {
    return null;
  }
}

export interface ExportInputs {
  project: EditProject;
  durations: SourceDurations;
  audioUrl: (source: EditAudioSource) => string;
  codecs: FastCodecs;
  onProgress?: (fraction: number) => void;
}

export async function exportEdit(inp: ExportInputs): Promise<RenderResult> {
  const { project: p, durations } = inp;
  const fps = p.output.fps;
  const frameMs = 1000 / fps;
  const { w, h } = frameSize(p.format, p.output.resolution);
  const totalMs = editDurationMs(p);
  const totalS = Math.max(1 / fps, totalMs / 1000);
  const inputs = new Map<string, Input>();
  // the codec the browser has at this size (a 1080p frame may need another than the 720p check found)
  const codecs = (await fastExportCodecs({ w, h })) ?? inp.codecs;
  // the straps' design, before the first frame that shows one
  await loadStrapArt(p.strapDesign ?? DEFAULT_STRAP_DESIGN);
  // the progress reaches the page once a percent: every frame would make it draw itself thousands of times
  let shownPct = -1;
  const progress = (f: number) => {
    const pct = Math.floor(f * 100);
    if (pct === shownPct) return;
    shownPct = pct;
    inp.onProgress?.(f);
  };
  try {
    const target = new BufferTarget();
    const output = new Output({ format: new Mp4OutputFormat({ fastStart: 'in-memory' }), target });
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const c = canvas.getContext('2d')!;
    const renderer = new FrameRenderer(w, h);
    const frames = new CanvasSource(canvas, { codec: codecs.video, quality: new Quality({ bitrate: bitrateFor(w, h) }), keyFrameInterval: 2 });
    output.addVideoTrack(frames, { frameRate: fps });
    const sound = new AudioBufferSource({ codec: codecs.audio, quality: new Quality({ bitrate: AUDIO_BITRATE }) });
    output.addAudioTrack(sound);
    await output.start();
    inp.onProgress?.(0.02);

    // ── the sound: the monitor's plan on an offline graph ──
    const plan = mixPlan(p, durations);
    const buffers = new Map<string, AudioBuffer | null>();
    for (const s of p.segments) {
      if (!pieceSounds(s)) continue;
      const key = sourceKey(s.source);
      if (!buffers.has(key)) buffers.set(key, await decodeSound(sourceUrlOf(s.source)));
    }
    for (const a of p.audio) {
      const key = audioSourceKey(a.source);
      if (!buffers.has(key)) buffers.set(key, await decodeSound(inp.audioUrl(a.source), { whole: isPieceSound(a.source) ? undefined : false }));
    }
    const rate = 48_000;
    const ctx = new OfflineAudioContext(2, Math.max(1, Math.ceil(totalS * rate)), rate);
    scheduleMix(ctx, plan, (key) => buffers.get(key) ?? null, 0, 0, ctx.destination);
    await sound.add(await ctx.startRendering());
    sound.close();
    inp.onProgress?.(0.1);

    // ── the photos, decoded once ──
    const stills = new Map<string, LayerImage | null>();
    const photoKeys = new Map<string, string>();
    for (const s of p.segments) if (s.source.kind === 'image') photoKeys.set(sourceKey(s.source), sourceUrlOf(s.source));
    for (const o of p.overlays) photoKeys.set(sourceKey(o.source), sourceUrlOf(o.source));
    for (const [key, url] of photoKeys) stills.set(key, await loadStill(url).catch(() => null));

    // ── the pictures: every frame of the edit, the frames each video piece must give, in order ──
    const count = Math.max(1, Math.round(totalS * fps));
    const specs: FrameSpec[] = [];
    const needs = new Map<number, number[]>();
    for (let k = 0; k < count; k++) {
      const spec = frameAt(p, Math.min(totalMs, k * frameMs), durations.video);
      specs.push(spec);
      for (const l of spec.layers) {
        const seg = p.segments[l.index]!;
        if (seg.source.kind === 'image') continue;
        const list = needs.get(l.index) ?? [];
        // a frozen frame needs its one frame once; the frames after reuse it
        if (seg.freeze && list.length) continue;
        list.push(l.sourceMs / 1000);
        needs.set(l.index, list);
      }
    }
    const streams = new Map<number, AsyncGenerator<WrappedCanvas | null, void, unknown>>();
    const last = new Map<number, WrappedCanvas>();
    for (const [index, times] of needs) {
      const src = p.segments[index]!.source;
      const url = sourceUrlOf(src);
      let input = inputs.get(url);
      if (!input) {
        input = await openSource(url);
        inputs.set(url, input);
      }
      const video = await input.getPrimaryVideoTrack();
      if (!video || !(await video.canDecode())) throw new Error('Ο browser δεν αποκωδικοποιεί ένα από τα video');
      // each frame at the source's own size: the renderer crops, fits and frames it
      const sink = new CanvasSink(video, { poolSize: 0 });
      streams.set(index, sink.canvasesAtTimestamps(times));
    }
    for (let k = 0; k < count; k++) {
      // a turn of the event loop every few frames keeps the progress painting and the page responsive
      if (k % 8 === 7) await breathe();
      const spec = specs[k]!;
      const images = new Map<number, WrappedCanvas>();
      for (const l of spec.layers) {
        const seg = p.segments[l.index]!;
        if (seg.source.kind === 'image') continue;
        let wc: WrappedCanvas | null = null;
        if (!(seg.freeze && last.has(l.index))) {
          const next = await streams.get(l.index)!.next();
          wc = next.done ? null : next.value;
        }
        if (wc) last.set(l.index, wc);
        const use = wc ?? last.get(l.index);
        if (use) images.set(l.index, use);
      }
      const tMs = Math.min(totalMs, k * frameMs);
      renderer.render(c, {
        project: p,
        // a hair past the frame's start, so an item that starts on it is on it
        tMs: tMs + 0.5,
        spec,
        imageFor: (l) => {
          const wc = images.get(l.index);
          return wc ? { img: wc.canvas, w: wc.canvas.width, h: wc.canvas.height } : null;
        },
        stillFor: (key) => stills.get(key) ?? null,
      });
      await frames.add(k / fps, 1 / fps);
      progress(0.1 + 0.88 * ((k + 1) / count));
    }
    for (const s of streams.values()) await s.return(undefined);
    frames.close();
    await output.finalize();
    if (!target.buffer) throw new Error('Το export δεν έγραψε αρχείο');
    inp.onProgress?.(1);
    return { blob: new Blob([target.buffer], { type: 'video/mp4' }), mime: 'video/mp4', durationMs: Math.round(totalS * 1000) };
  } finally {
    for (const i of inputs.values()) i.dispose();
  }
}
